/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { Navigate } from 'react-router-dom'
import React from 'react'

import DashboardLayout from '@/layouts/Dashboard'
import ErrorLayout from '@/layouts/Error'
import Page404 from '@/pages/auth/Page404'
import Page500 from '@/pages/auth/Page500'
import Classification from '@/pages/labeling/Classification'
import Correction from '@/pages/labeling/Correction'
import Detection from '@/pages/labeling/Detection'
import LabelingExplorer from '@/pages/labeling/LabelingExplorer'
import DAIZeroDatasetReview from '@/pages/labeling/DAIZeroDatasetReview'
import Review from '@/pages/labeling/Review'
import Stats from '@/pages/labeling/Stats'
import { NotAuthorized } from '@/pages/pages/NotAuthorized'
import UserContext from './context/UserContext'

const AuthRedirect = () => <Navigate to="/stats" replace />

const SuperuserOnly = ({ children }: { children: React.ReactElement }) => {
  const { user, loading } = React.useContext(UserContext)

  if (loading) {
    return null
  }

  return user?.isSuperuser ? (
    children
  ) : (
    <Navigate to="/not-authorized" replace />
  )
}

const routes = [
  {
    path: '/',
    element: <AuthRedirect />, // redirects to /stats
  },
  {
    path: '/not-authorized',
    element: <NotAuthorized />,
  },
  {
    path: '',
    element: <DashboardLayout />,
    children: [
      { path: 'stats', element: <Stats /> },
      { path: 'classification', element: <Classification /> },
      { path: 'correction', element: <Correction /> },
      { path: 'detection', element: <Detection /> },
      {
        path: 'explorer',
        element: (
          <SuperuserOnly>
            <LabelingExplorer />
          </SuperuserOnly>
        ),
      },
      {
        path: 'crop-review',
        element: (
          <SuperuserOnly>
            <DAIZeroDatasetReview />
          </SuperuserOnly>
        ),
      },
      {
        path: 'dai-zero-dataset',
        element: <Navigate to="/crop-review" replace />,
      },
      { path: 'review', element: <Review /> },
      { path: '', element: <Navigate to="/stats" replace /> },
    ],
  },
  {
    path: 'error',
    element: <ErrorLayout />,
    children: [
      { path: '404', element: <Page404 /> },
      { path: '500', element: <Page500 /> },
      { path: 'not-authorized', element: <NotAuthorized /> },
    ],
  },
  {
    path: '*',
    element: <ErrorLayout />,
    children: [{ path: '*', element: <Page404 /> }],
  },
]

export default routes
